/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import EditorNotification from 'bundles/personalization-settings/components/EditorNotification';
import {
  PERSONALIZATION_SETTINGS_FORM,
  useTrackPersonalizationModalClose,
  useTrackPersonalizationModalView,
} from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';

import _t from 'i18n!nls/personalization-settings';

const MODAL_NAME = 'personalization_settings_add_current_role';
const DIALOG_CONTENT_ID = 'add-current-role-to-desired-roles-dialog-content';

const styles = {
  cancelButton: css`
    margin-left: var(--cds-spacing-150);
  `,
};

export type AddCurrentRoleToDesiredRolesModalProps = {
  open: boolean;
  /** The current role's display name, shown in the prompt copy. */
  roleName: string;
  /** True while the confirm save is in flight; disables actions and shows a spinner on Add. */
  loading?: boolean;
  /** Set once the confirm save resolves, so the learner sees the outcome before the dialog closes. */
  notificationType?: 'success' | 'error';
  onConfirm: () => void;
  onDismiss: () => void;
};

/**
 * Prompts the learner to add their current role to their desired roles list,
 * shown after they select "Grow in my current role" as their learning goal
 * (see `LearnerObjectSettingItems`, which decides when to open this).
 */
const AddCurrentRoleToDesiredRolesModal = ({
  open,
  roleName,
  loading = false,
  notificationType,
  onConfirm,
  onDismiss,
}: AddCurrentRoleToDesiredRolesModalProps) => {
  const { Content, HeadingGroup, Actions } = Dialog;
  const track = useTracker();
  const trackModalView = useTrackPersonalizationModalView();
  const trackModalClose = useTrackPersonalizationModalClose();

  React.useEffect(() => {
    if (open) trackModalView(MODAL_NAME);
  }, [open, trackModalView]);

  const handleConfirm = () => {
    track('click_button', {
      form: PERSONALIZATION_SETTINGS_FORM,
      modal: { name: MODAL_NAME },
      button: { name: 'personalization_settings_confirm_add_current_role' },
    });
    onConfirm();
  };

  const isConfirmed = notificationType === 'success';
  const actionsDisabled = loading || isConfirmed;

  const handleDismiss = () => {
    if (actionsDisabled) return;
    trackModalClose(MODAL_NAME);
    onDismiss();
  };

  return (
    <Dialog aria-describedby={DIALOG_CONTENT_ID} open={open} variant="standard" onClose={handleDismiss}>
      <HeadingGroup>{_t('Add current role to desired roles?')}</HeadingGroup>

      <Content id={DIALOG_CONTENT_ID}>
        <Typography2 variant="bodyPrimary" component="p">
          {_t('Add "#{roleName}" to your desired roles so we can tailor recommendations to help you grow in it?', {
            roleName,
          })}
        </Typography2>
        <div aria-live="polite">{notificationType && <EditorNotification type={notificationType} />}</div>
      </Content>

      <Actions>
        <Button size="medium" variant="primary" onClick={handleConfirm} loading={loading} disabled={actionsDisabled}>
          {_t('Add')}
        </Button>
        <Button
          size="medium"
          variant="secondary"
          onClick={handleDismiss}
          disabled={actionsDisabled}
          css={styles.cancelButton}
        >
          {_t('Skip')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default AddCurrentRoleToDesiredRolesModal;
